import type {
  EscrituraKanbanStatus,
  KanbanColumnConfig,
  KanbanColumnQueryConfig,
  KanbanSort,
  KanbanSortByColumn,
  KanbanSortOption,
  TramiteKanbanItem,
} from './types';

/**
 * Prefijo de las query keys del tablero. Cada columna agrega su id, los filtros
 * y el orden, así que invalidar por este prefijo alcanza a todas.
 */
export const KANBAN_QUERY_KEY = ['tramites', 'kanban'] as const;

export const KANBAN_COLUMNS: KanbanColumnConfig[] = [
  {
    id: 'sin_firmar',
    title: 'Sin firmar',
    accentClassName: 'border-t-amber-500',
    dotClassName: 'bg-amber-500',
  },
  {
    id: 'firmada_sin_presentar',
    title: 'Firmada sin presentar',
    accentClassName: 'border-t-orange-500',
    dotClassName: 'bg-orange-500',
  },
  {
    id: 'firmada_presentada',
    title: 'Firmada y presentada',
    accentClassName: 'border-t-teal-500',
    dotClassName: 'bg-teal-500',
  },
  {
    id: 'inscripta',
    title: 'Inscripta / Finalizada',
    accentClassName: 'border-t-emerald-600',
    dotClassName: 'bg-emerald-600',
  },
  {
    id: 'bloqueada',
    title: 'Bloqueada',
    accentClassName: 'border-t-red-600',
    dotClassName: 'bg-red-600',
  },
];

/**
 * Cada columna se pide al backend por separado.
 * `escrituraFilter` usa la nomenclatura del backend (`firmado_*`), que difiere
 * de los ids del kanban (`firmada_*`), y el orden es el de la fecha que muestra
 * la card de esa columna.
 */
export const KANBAN_COLUMN_QUERIES: Record<
  EscrituraKanbanStatus,
  KanbanColumnQueryConfig
> = {
  sin_firmar: {
    escrituraFilter: 'sin_firmar',
    sortField: 'fecfirma',
    sortDirection: 'asc',
  },
  firmada_sin_presentar: {
    escrituraFilter: 'firmado_sin_presentar',
    sortField: 'fecescritura',
    sortDirection: 'asc',
  },
  firmada_presentada: {
    escrituraFilter: 'firmado_presentado',
    sortField: 'created_at',
    sortDirection: 'desc',
  },
  inscripta: {
    escrituraFilter: 'inscripta',
    sortField: 'created_at',
    sortDirection: 'desc',
  },
  // El bloqueo pisa el avance de la escritura: el backend la saca de las otras
  // cuatro columnas cuando la vista es kanban.
  bloqueada: {
    escrituraFilter: 'bloqueada',
    sortField: 'bloqueado_at',
    sortDirection: 'desc',
  },
};

/**
 * Columnas visibles según el filtro global "Bloqueada".
 *
 * El filtro sigue disponible en el tablero, pero ahora elige columnas en vez de
 * filtrar dentro de ellas: pedir sólo bloqueadas con las otras cuatro en
 * pantalla dejaría cuatro columnas vacías.
 */
export const getVisibleKanbanColumns = (
  bloqueadaFilter?: string,
): KanbanColumnConfig[] => {
  if (bloqueadaFilter === 'si') {
    return KANBAN_COLUMNS.filter((column) => column.id === 'bloqueada');
  }

  if (bloqueadaFilter === 'no') {
    return KANBAN_COLUMNS.filter((column) => column.id !== 'bloqueada');
  }

  return KANBAN_COLUMNS;
};

/**
 * Criterios ofrecidos en el menú de orden de cada columna.
 * Todos los `sortField` tienen que estar en la whitelist `$allowedSorts` del
 * backend (`TramiteController::index`), o la request cae silenciosamente a
 * `created_at`.
 */
export const KANBAN_SORT_OPTIONS: KanbanSortOption[] = [
  { sortField: 'fecfirma', label: 'Fecha de firma', defaultDirection: 'asc' },
  { sortField: 'fecescritura', label: 'Fecha de escritura', defaultDirection: 'asc' },
  { sortField: 'fecini', label: 'Fecha de inicio', defaultDirection: 'asc' },
  {
    sortField: 'vencimientoPresentacion',
    label: 'Vencimiento de presentación',
    defaultDirection: 'asc',
  },
  { sortField: 'num_escritura', label: '# Escritura', defaultDirection: 'desc' },
  { sortField: 'propiedad', label: 'Propiedad (A-Z)', defaultDirection: 'asc' },
  { sortField: 'created_at', label: 'Más recientes', defaultDirection: 'desc' },
  {
    sortField: 'bloqueado_at',
    label: 'Fecha de bloqueo',
    defaultDirection: 'desc',
    // Fuera de la columna "Bloqueada" la fecha es siempre nula y el criterio no
    // ordena nada.
    columns: ['bloqueada'],
  },
];

export const getKanbanSortOption = (sortField: string) =>
  KANBAN_SORT_OPTIONS.find((option) => option.sortField === sortField);

/** Criterios de orden ofrecidos en una columna concreta */
export const getColumnSortOptions = (status: EscrituraKanbanStatus) =>
  KANBAN_SORT_OPTIONS.filter(
    (option) => !option.columns || option.columns.includes(status),
  );

export const getDefaultColumnSort = (status: EscrituraKanbanStatus): KanbanSort => {
  const { sortField, sortDirection } = KANBAN_COLUMN_QUERIES[status];
  return { sortField, sortDirection };
};

/** Devuelve el orden elegido por el usuario para esa columna, o el de fábrica */
export const resolveColumnSort = (
  status: EscrituraKanbanStatus,
  overrides: KanbanSortByColumn = {},
): KanbanSort => overrides[status] ?? getDefaultColumnSort(status);

/**
 * Las preferencias son JSON libre guardado en el server, así que se descartan
 * las columnas desconocidas y los criterios que el backend ya no acepta.
 */
export const parseStoredKanbanSort = (stored: unknown): KanbanSortByColumn => {
  if (!stored || typeof stored !== 'object') return {};

  const validColumns = new Set(KANBAN_COLUMNS.map((column) => column.id));

  return Object.entries(stored as Record<string, unknown>).reduce<KanbanSortByColumn>(
    (result, [status, value]) => {
      if (!validColumns.has(status as EscrituraKanbanStatus)) return result;

      const { sortField, sortDirection } = (value ?? {}) as Partial<KanbanSort>;
      if (!sortField) return result;
      // No alcanza con que el criterio exista: tiene que estar habilitado para
      // esa columna, o el menú mostraría una opción que no ofrece.
      const columnOptions = getColumnSortOptions(status as EscrituraKanbanStatus);
      if (!columnOptions.some((option) => option.sortField === sortField)) return result;
      if (sortDirection !== 'asc' && sortDirection !== 'desc') return result;

      result[status as EscrituraKanbanStatus] = { sortField, sortDirection };
      return result;
    },
    {},
  );
};

export const getEscrituraKanbanStatus = (
  tramite: Pick<
    TramiteKanbanItem,
    | 'bloqueado'
    | 'estado'
    | 'escritura_path'
    | 'escritura_pub_path'
    | 'fecentrada'
    | 'num_entrada'
  >,
): EscrituraKanbanStatus => {
  // Primero que todo: una escritura bloqueada sólo vive en su propia columna,
  // igual que lo resuelve el backend para la vista kanban.
  if (tramite.bloqueado) {
    return 'bloqueada';
  }

  // Un trámite finalizado ya no tiene avance pendiente: comparte columna con
  // las inscriptas, esté o no inscripto.
  if (tramite.escritura_pub_path || tramite.estado === 'finalizado') {
    return 'inscripta';
  }

  if (
    tramite.escritura_path &&
    tramite.fecentrada &&
    tramite.num_entrada
  ) {
    return 'firmada_presentada';
  }

  if (tramite.escritura_path) {
    return 'firmada_sin_presentar';
  }

  return 'sin_firmar';
};
